import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../api';
import { SectionHeader } from '../../components/PageHeader';
import { useToast } from '../../toast';

interface Workspace {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  retentionMonths: number;
  retentionMaxMonths: number;
  events: number;
  oldestEvent: string | null;
  members: number;
}

interface Page {
  tenants: Workspace[];
  floorMonths: number;
  ceilingMonths: number;
}

/**
 * Every workspace on the platform, and what its audit log is allowed to cost.
 *
 * Deliberately shallow: names, sizes and retention. Nothing here reads inside anybody's log, because an
 * operator needing to know how much a tenant keeps is not a reason to see who went into which org.
 *
 * Raising a ceiling is the commercial half of retention. A tenant picks a number inside it; a customer who
 * needs longer than their ceiling writes to us, and this is where the answer is given.
 */
export function Workspaces() {
  const [page, setPage] = useState<Page | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, number>>({});

  const load = useCallback(async () => {
    setError(null);
    try {
      setPage(await api<Page>('/api/admin/tenants'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not list the workspaces');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The heading stands whether or not the list arrived, so a failure reads as this page's failure rather
  // than as an empty space under the console's title.
  const header = (
    <SectionHeader
      title="Workspaces"
      blurb="Every workspace on the platform, how big its audit log has grown, and the longest it may keep it."
    />
  );

  if (!page) {
    return (
      <>
        {header}
        {error ? <div className="alert">{error}</div> : <p className="muted">Loading…</p>}
      </>
    );
  }

  return (
    <>
      {header}
      {error && <div className="alert">{error}</div>}

      <p className="lede">
        A workspace keeps its audit log for as long as it chooses, between {page.floorMonths} months and the
        ceiling set here. The floor is the platform's and is not for sale: a log its own subject can shorten
        at will is not evidence of anything.
      </p>

      <div className="card flush">
        <table>
          <thead>
            <tr><th>Workspace</th><th>People</th><th>Events</th><th>Keeping</th><th>Ceiling</th><th className="actions-cell"></th></tr>
          </thead>
          <tbody>
            {page.tenants.length === 0 && (
              <tr><td colSpan={6} className="empty">No workspaces yet.</td></tr>
            )}
            {page.tenants.map((t) => {
              const pending = edits[t.id] ?? t.retentionMaxMonths;
              return (
                <tr key={t.id}>
                  <td>
                    {t.name}
                    <br />
                    <span className="small muted mono">{t.slug}</span>
                  </td>
                  <td className="small">{t.members}</td>
                  <td className="small">
                    {t.events.toLocaleString()}
                    {t.oldestEvent && (
                      <>
                        <br />
                        <span className="muted">since {new Date(t.oldestEvent).toLocaleDateString()}</span>
                      </>
                    )}
                  </td>
                  <td className="small">
                    {t.retentionMonths} months
                    {t.retentionMonths === t.retentionMaxMonths && t.retentionMaxMonths > page.floorMonths && (
                      <>
                        <br />
                        <span className="muted">at their ceiling</span>
                      </>
                    )}
                  </td>
                  <td>
                    <input
                      type="number"
                      min={page.floorMonths}
                      max={page.ceilingMonths}
                      step={1}
                      value={pending}
                      onChange={(e) => setEdits({ ...edits, [t.id]: Number(e.target.value) })}
                      style={{ width: 80 }}
                    />
                  </td>
                  <td className="actions-cell">
                    <button
                      className="secondary"
                      disabled={busy === t.id || pending === t.retentionMaxMonths}
                      onClick={() => {
                        setBusy(t.id);
                        setError(null);
                        api<{ months: number; maxMonths: number }>(`/api/admin/tenants/${t.id}/audit-retention-max`, {
                          method: 'PATCH',
                          body: { maxMonths: pending },
                        })
                          .then(async (r) => {
                            toast.ok(
                              r.months < pending
                                ? `${t.name} may now keep up to ${r.maxMonths} months, and is keeping ${r.months}.`
                                : `${t.name} may now keep up to ${r.maxMonths} months.`,
                            );
                            setEdits(({ [t.id]: _dropped, ...rest }) => rest);
                            await load();
                          })
                          .catch((err: unknown) => toast.error(err instanceof ApiError ? err.message : 'Could not save'))
                          .finally(() => setBusy(null));
                      }}
                    >
                      Save
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="small muted">
        Lowering a ceiling below what a workspace has already chosen pulls their own setting down with it,
        and the next night's purge will act on the shorter period. Say so before you do it.
      </p>
    </>
  );
}
